/**
 * Company Email Templates
 *
 * Lets company managers customize the subject line and the plain-text message of 4
 * company-scoped email templates. Text only: no HTML, and links come only from system
 * variables and the system-added button (SC-439, Q17). The server renders and enforces the
 * rules; the preview shows its HTML in a fully sandboxed iframe. Colours and logo come from
 * company branding, not from this editor.
 *
 * API: /api/email/company-templates/:companyId
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import {
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Info as InfoIcon,
  Preview as PreviewIcon,
  RestoreOutlined as RestoreIcon,
  Save as SaveIcon,
  Send as SendIcon,
  Subject as SubjectIcon,
} from '@mui/icons-material'
import {
  COMPANY_TEMPLATE_BODY_MAX,
  COMPANY_TEMPLATE_SUBJECT_MAX,
  COMPANY_TEMPLATE_TEST_MAX_RECIPIENTS,
  CompanyTemplateApiError,
  fetchCompanyTemplates,
  parseRecipientEmails,
  previewCompanyTemplate,
  revertCompanyTemplate,
  saveCompanyTemplate,
  sendCompanyTemplateTestEmail,
  type CompanyTemplateSlot,
  type TestEmailResult,
} from './companyTemplatesApi'
import { sanitizeEmailPreviewHtmlForIframe } from './previewHtmlSanitize'
import { useSurfaceState } from '../../hooks/usePermission'

interface CompanyEmailTemplatesProps {
  companyId: string
  /**
   * True only when the platform admin view opens the panel. That view is entered on the account
   * status or verified capabilities, which can disagree with the surface answer while capabilities
   * are loading or failed; waiting for it would leave a platform admin a blank dialog. The server
   * stays the guard (a 403 shows the message below). Company managers never set it.
   */
  elevatedView?: boolean
}

interface DisplayError {
  message: string
  items: string[]
}

function toDisplayError(e: unknown, fallback: string): DisplayError {
  if (e instanceof CompanyTemplateApiError) return { message: e.message, items: e.items }
  return { message: (e as Error)?.message || fallback, items: [] }
}

const TEMPLATE_META: Record<string, { label: string; icon: string }> = {
  'company-invitation': { label: 'Company Invitation', icon: '✉️' },
  'company-removal': { label: 'Company Removal', icon: '🚪' },
  'account-welcome': { label: 'Account Welcome', icon: '👋' },
  'account-created': { label: 'Account Created', icon: '🎉' },
}

const ErrorAlert: React.FC<{ error: DisplayError; onClose?: () => void }> = ({ error, onClose }) => (
  <Alert severity="error" onClose={onClose} sx={{ fontSize: 12 }}>
    {error.message}
    {error.items.length > 0 && (
      <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
        {error.items.map((it) => (
          <li key={it}>{it}</li>
        ))}
      </Box>
    )}
  </Alert>
)

// ── Main Component ─────────────────────────────────────────────────────────────

const CompanyEmailTemplates: React.FC<CompanyEmailTemplatesProps> = ({
  companyId,
  elevatedView = false,
}) => {
  const theme = useTheme()
  // Someone without email_templates.manage gets nothing and no request (SC-491). The server still
  // refuses a 403 if the permission changes while this is open; that message stays as the fallback.
  const templatesPermission = useSurfaceState('manager-view.email-templates-tab')
  const canLoad = elevatedView || (!templatesPermission.loading && templatesPermission.allowed)
  const [templates, setTemplates] = useState<CompanyTemplateSlot[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [tab, setTab] = useState<'editor' | 'preview'>('editor')

  // Editor state. Empty draft = use the platform default.
  const [subjectDraft, setSubjectDraft] = useState('')
  const [bodyDraft, setBodyDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [reverting, setReverting] = useState(false)
  const [saveError, setSaveError] = useState<DisplayError | null>(null)
  const [previewHtml, setPreviewHtml] = useState('')
  const [previewSubject, setPreviewSubject] = useState('')
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<DisplayError | null>(null)

  // Snackbar
  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: 'success' | 'error' }>({
    open: false,
    msg: '',
    severity: 'success',
  })

  // Test email state
  const [testEmailInput, setTestEmailInput] = useState('')
  const [testSending, setTestSending] = useState(false)
  const [testResults, setTestResults] = useState<TestEmailResult[] | null>(null)
  const [testError, setTestError] = useState<DisplayError | null>(null)

  const bodyRef = useRef<HTMLTextAreaElement>(null)
  const subjectRef = useRef<HTMLInputElement>(null)
  const lastFocusedField = useRef<'subject' | 'body'>('body')

  // One list call on open (and one after save/revert); preview and test only on demand.
  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setTemplates(await fetchCompanyTemplates(companyId))
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [companyId])

  useEffect(() => {
    if (!canLoad) return
    void load()
  }, [load, canLoad])

  const seedDrafts = (tpl: CompanyTemplateSlot) => {
    setSubjectDraft(tpl.override?.subjectOverride || '')
    setBodyDraft(tpl.override?.bodyText || '')
  }

  const selectTemplate = (key: string) => {
    const tpl = templates.find((t) => t.templateKey === key)
    if (!tpl) return
    setSelectedKey(key)
    seedDrafts(tpl)
    setPreviewHtml('')
    setPreviewSubject('')
    setPreviewError(null)
    setSaveError(null)
    setTab('editor')
    setTestResults(null)
    setTestError(null)
  }

  const selectedTemplate = templates.find((t) => t.templateKey === selectedKey)

  const recipients = parseRecipientEmails(testEmailInput)
  const tooManyRecipients = recipients.length > COMPANY_TEMPLATE_TEST_MAX_RECIPIENTS

  const handleSendTestEmail = async () => {
    if (!selectedKey || tooManyRecipients) return
    setTestSending(true)
    setTestResults(null)
    setTestError(null)
    try {
      const { results } = await sendCompanyTemplateTestEmail(companyId, selectedKey, recipients)
      setTestResults(results)
      const allOk = results.every((r) => r.success)
      setSnack({
        open: true,
        msg: allOk
          ? `Test email sent to ${results.map((r) => r.email).join(', ')}`
          : 'Some test emails failed - check results below',
        severity: allOk ? 'success' : 'error',
      })
    } catch (e) {
      // not_member lists the offending addresses; shown inline so they stay visible.
      setTestError(toDisplayError(e, 'Test send failed'))
    } finally {
      setTestSending(false)
    }
  }

  const bodyTooLong = bodyDraft.length > COMPANY_TEMPLATE_BODY_MAX

  const isDirty =
    selectedTemplate &&
    (subjectDraft !== (selectedTemplate.override?.subjectOverride || '') ||
      bodyDraft !== (selectedTemplate.override?.bodyText || ''))

  // Insert {{var}} at the cursor of the field that was last focused (subject or body).
  const insertVariable = (name: string) => {
    const tag = `{{${name}}}`
    const inSubject = lastFocusedField.current === 'subject'
    const el = inSubject ? subjectRef.current : bodyRef.current
    const value = inSubject ? subjectDraft : bodyDraft
    const setValue = inSubject ? setSubjectDraft : setBodyDraft
    const start = el?.selectionStart ?? value.length
    const end = el?.selectionEnd ?? value.length
    setValue(value.slice(0, start) + tag + value.slice(end))
    if (!el) return
    setTimeout(() => {
      el.selectionStart = start + tag.length
      el.selectionEnd = start + tag.length
      el.focus()
    }, 0)
  }

  const handleSave = async () => {
    if (!selectedKey) return
    setSaving(true)
    setSaveError(null)
    try {
      await saveCompanyTemplate(companyId, selectedKey, subjectDraft, bodyDraft)
      setSnack({ open: true, msg: 'Template saved successfully.', severity: 'success' })
      // Refresh the baseline (updatedAt, hasOverride) without unmounting the editor.
      const updated = await fetchCompanyTemplates(companyId)
      setTemplates(updated)
      const tpl = updated.find((t) => t.templateKey === selectedKey)
      if (tpl) seedDrafts(tpl)
    } catch (e) {
      setSaveError(toDisplayError(e, 'Save failed.'))
    } finally {
      setSaving(false)
    }
  }

  const handleRevert = async () => {
    if (!selectedKey) return
    setReverting(true)
    setSaveError(null)
    try {
      await revertCompanyTemplate(companyId, selectedKey)
      setSnack({ open: true, msg: 'Template reverted to default.', severity: 'success' })
      setTemplates(await fetchCompanyTemplates(companyId))
      setSubjectDraft('')
      setBodyDraft('')
      setPreviewHtml('')
      setPreviewSubject('')
    } catch (e: any) {
      setSnack({ open: true, msg: e.message || 'Revert failed.', severity: 'error' })
    } finally {
      setReverting(false)
    }
  }

  const handlePreview = async () => {
    if (!selectedKey) return
    setPreviewLoading(true)
    setPreviewError(null)
    setTab('preview')
    try {
      const result = await previewCompanyTemplate(companyId, selectedKey, subjectDraft, bodyDraft)
      // The server renders escaped text; sanitizing again is defence in depth before srcDoc.
      setPreviewHtml(sanitizeEmailPreviewHtmlForIframe(result.html))
      setPreviewSubject(result.subject)
    } catch (e) {
      setPreviewHtml('')
      setPreviewError(toDisplayError(e, 'Preview failed.'))
    } finally {
      setPreviewLoading(false)
    }
  }

  const meta = selectedKey ? TEMPLATE_META[selectedKey] : null

  // ── Layout ────────────────────────────────────────────────────────────────

  // After every hook above. Hidden, not disabled, while the answer is loading and when denied.
  if (!canLoad) return null

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" py={8}>
        <CircularProgress sx={{ color: 'primary.main' }} size={36} />
        <Typography sx={{ ml: 2, color: 'text.secondary' }}>Loading email templates…</Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box p={3}>
        {/* No close button: dismissing it would show the editor with an empty list, which reads
            as "no templates" when the list never loaded. */}
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void load()}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', height: '100%', minHeight: 500, overflow: 'hidden' }}>
      {/* ── Left panel: template list ────────────────────────────────────── */}
      <Box
        sx={{
          width: 220,
          flexShrink: 0,
          borderRight: '1px solid',
          borderColor: 'divider',
          overflowY: 'auto',
          bgcolor: 'action.hover',
          backgroundImage: 'none',
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Email Templates
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            {templates.length} customizable
          </Typography>
        </Box>

        {templates.map((tpl) => {
          const m = TEMPLATE_META[tpl.templateKey]
          const isSelected = selectedKey === tpl.templateKey
          return (
            <Box
              key={tpl.templateKey}
              onClick={() => selectTemplate(tpl.templateKey)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.5,
                cursor: 'pointer',
                borderLeft: isSelected ? '3px solid' : '3px solid transparent',
                borderLeftColor: isSelected ? 'primary.main' : 'transparent',
                bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                backgroundImage: 'none',
                '&:hover': {
                  bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.12) : 'action.hover',
                  backgroundImage: 'none',
                },
                transition: 'all 0.15s',
              }}
            >
              <Box sx={{ fontSize: 16, flexShrink: 0 }}>{m?.icon || '📧'}</Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isSelected ? 600 : 400,
                    color: isSelected ? 'primary.main' : 'text.primary',
                    fontSize: 13,
                    lineHeight: 1.3,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {m?.label || tpl.templateKey}
                </Typography>
                {tpl.hasOverride && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                    <CheckCircleIcon sx={{ fontSize: 10, color: 'success.main' }} />
                    <Typography variant="caption" sx={{ color: 'success.main', fontSize: 10 }}>
                      Customized
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )
        })}
      </Box>

      {/* ── Right panel: editor or placeholder ───────────────────────────── */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!selectedTemplate ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            flex={1}
            sx={{ color: 'text.disabled', gap: 1 }}
          >
            <EmailIcon sx={{ fontSize: 48, color: 'action.disabled' }} />
            <Typography variant="body2" sx={{ color: 'text.disabled' }}>
              Select a template to customize
            </Typography>
          </Box>
        ) : (
          <>
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'action.hover',
                backgroundImage: 'none',
                flexShrink: 0,
              }}
            >
              <Box sx={{ fontSize: 18 }}>{meta?.icon || '📧'}</Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: 14 }}>
                  {meta?.label || selectedKey}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 11 }}>
                  {selectedTemplate.description}
                </Typography>
              </Box>
              {selectedTemplate.hasOverride && (
                <Chip
                  label="Customized"
                  size="small"
                  icon={<CheckCircleIcon />}
                  sx={{
                    bgcolor: alpha(theme.palette.success.main, 0.15),
                    backgroundImage: 'none',
                    color: 'success.dark',
                    fontSize: 11,
                    height: 22,
                    '& .MuiChip-icon': { fontSize: 12, color: 'success.main' },
                  }}
                />
              )}
            </Box>

            {selectedKey === 'company-invitation' && (
              <Alert severity="warning" sx={{ mx: 2, mt: 1.5, flexShrink: 0, fontSize: 12 }}>
                Preview and test sends use a non-redeemable sample invitation link. They cannot sign anyone in.
                To test accepting an invitation, use <strong>Company Management</strong> → invite a user.
              </Alert>
            )}

            {/* Tabs */}
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              textColor="primary"
              indicatorColor="primary"
              sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                minHeight: 38,
                flexShrink: 0,
                px: 2,
                '& .MuiTab-root': { minHeight: 38, fontSize: 12, textTransform: 'none', fontWeight: 500, py: 0.5 },
              }}
            >
              <Tab icon={<EditIcon sx={{ fontSize: 14 }} />} iconPosition="start" label="Editor" value="editor" />
              <Tab icon={<PreviewIcon sx={{ fontSize: 14 }} />} iconPosition="start" label="Preview" value="preview" onClick={handlePreview} />
            </Tabs>

            {/* Editor panel */}
            {tab === 'editor' && (
              <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>

                {/* Info callout */}
                <Alert
                  severity="info"
                  icon={<InfoIcon />}
                  sx={{
                    fontSize: 12,
                    py: 0.5,
                    bgcolor: alpha(theme.palette.info.main, 0.08),
                    backgroundImage: 'none',
                    border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
                    color: 'text.primary',
                    '& .MuiAlert-icon': { fontSize: 16, color: 'info.main' },
                  }}
                >
                  Plain text only: no HTML and no links. Leave a field empty to use the platform default.
                  The action button, its link and the security notes are added by the system, and the logo
                  and colours come from your company branding.
                </Alert>

                {saveError && <ErrorAlert error={saveError} onClose={() => setSaveError(null)} />}

                {/* Variables */}
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.75, display: 'block', textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.5 }}>
                    Variables
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedTemplate.textVars.map((v) => (
                      <Tooltip key={v} title={`Insert {{${v}}} at the cursor`} placement="top" arrow>
                        <Chip
                          label={`{{${v}}}`}
                          size="small"
                          // Keep the text field focused so the cursor position survives the click.
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => insertVariable(v)}
                          sx={{
                            fontSize: 10,
                            height: 22,
                            fontFamily: 'monospace',
                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                            backgroundImage: 'none',
                            color: 'primary.dark',
                            border: '1px solid',
                            borderColor: alpha(theme.palette.primary.main, 0.35),
                            cursor: 'pointer',
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2), backgroundImage: 'none' },
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 11 }}>
                    Only these variables work in the subject and the message. Click one to insert it at the cursor.
                  </Typography>
                </Box>

                {/* Subject */}
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block', textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.5 }}>
                    Subject Line
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    inputRef={subjectRef}
                    value={subjectDraft}
                    onChange={(e) => setSubjectDraft(e.target.value)}
                    onFocus={() => { lastFocusedField.current = 'subject' }}
                    placeholder={selectedTemplate.defaultSubject}
                    inputProps={{ maxLength: COMPANY_TEMPLATE_SUBJECT_MAX }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SubjectIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                      },
                    }}
                  />
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 11 }}>
                    One line, up to {COMPANY_TEMPLATE_SUBJECT_MAX} characters, no links. Default: {selectedTemplate.defaultSubject}
                  </Typography>
                </Box>

                {/* Message text */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block', textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.5 }}>
                    Message Text
                  </Typography>
                  <TextField
                    inputRef={bodyRef}
                    fullWidth
                    multiline
                    minRows={10}
                    maxRows={22}
                    value={bodyDraft}
                    error={bodyTooLong}
                    onChange={(e) => setBodyDraft(e.target.value)}
                    onFocus={() => { lastFocusedField.current = 'body' }}
                    placeholder={selectedTemplate.defaultBodyText}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        fontSize: 13,
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: bodyTooLong ? 'error.main' : 'text.disabled', fontSize: 11, display: 'block' }}
                  >
                    {bodyDraft.length}/{COMPANY_TEMPLATE_BODY_MAX} characters. A blank line starts a new paragraph.
                    Links, URLs and web addresses are not allowed (e-mail addresses are fine). The greyed text is the
                    platform default.
                  </Typography>
                </Box>

                <Divider />

                {/* Action bar */}
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  {selectedTemplate.hasOverride && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={reverting ? <CircularProgress size={12} /> : <RestoreIcon />}
                      disabled={reverting || saving}
                      onClick={handleRevert}
                      sx={{
                        borderColor: 'error.main',
                        color: 'error.main',
                        textTransform: 'none',
                        borderRadius: '8px',
                        '&:hover': {
                          borderColor: 'error.dark',
                          bgcolor: alpha(theme.palette.error.main, 0.06),
                          backgroundImage: 'none',
                        },
                      }}
                    >
                      Revert to Default
                    </Button>
                  )}

                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PreviewIcon />}
                    onClick={handlePreview}
                    sx={{
                      borderColor: 'info.main',
                      color: 'info.main',
                      textTransform: 'none',
                      borderRadius: '8px',
                      '&:hover': {
                        borderColor: 'info.dark',
                        bgcolor: alpha(theme.palette.info.main, 0.06),
                        backgroundImage: 'none',
                      },
                    }}
                  >
                    Preview
                  </Button>

                  <Button
                    variant="contained"
                    size="small"
                    startIcon={saving ? <CircularProgress size={12} color="inherit" /> : <SaveIcon />}
                    disabled={saving || !isDirty || bodyTooLong}
                    onClick={handleSave}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                      color: 'primary.contrastText',
                      textTransform: 'none',
                      borderRadius: '8px',
                      boxShadow: `0 4px 14px 0 ${alpha(theme.palette.primary.main, 0.3)}`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      },
                      '&.Mui-disabled': {
                        background: theme.palette.action.disabledBackground,
                        color: 'text.disabled',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    Save Changes
                  </Button>
                </Box>

                <Divider />

                {/* Send Test Email */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.5 }}>
                    Send Test Email
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <TextField
                      size="small"
                      placeholder="Empty = send to me"
                      value={testEmailInput}
                      error={tooManyRecipients}
                      onChange={(e) => { setTestEmailInput(e.target.value); setTestResults(null); setTestError(null) }}
                      helperText={
                        tooManyRecipients
                          ? `At most ${COMPANY_TEMPLATE_TEST_MAX_RECIPIENTS} addresses.`
                          : undefined
                      }
                      sx={{
                        flex: 1,
                        minWidth: 220,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          fontSize: 13,
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                        },
                      }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={testSending ? <CircularProgress size={12} sx={{ color: 'primary.main' }} /> : <SendIcon />}
                      disabled={testSending || tooManyRecipients}
                      onClick={handleSendTestEmail}
                      sx={{
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        textTransform: 'none',
                        borderRadius: '8px',
                        whiteSpace: 'nowrap',
                        '&:hover': {
                          borderColor: 'primary.dark',
                          bgcolor: alpha(theme.palette.primary.main, 0.06),
                          backgroundImage: 'none',
                        },
                      }}
                    >
                      {testSending ? 'Sending…' : 'Send Test'}
                    </Button>
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 11 }}>
                    {recipients.length === 0
                      ? 'With no address, the test goes to you (your own e-mail). '
                      : ''}
                    Only active members of this company can receive a test (max {COMPANY_TEMPLATE_TEST_MAX_RECIPIENTS}, separated by commas).
                    It uses the saved text and the company branding{isDirty ? '; save your changes first to include them' : ''}.
                  </Typography>
                  {testError && <ErrorAlert error={testError} onClose={() => setTestError(null)} />}
                  {testResults && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                      {testResults.map((r) => (
                        <Box
                          key={r.email}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.5,
                            py: 0.75,
                            borderRadius: '6px',
                            bgcolor: r.success
                              ? alpha(theme.palette.success.main, 0.12)
                              : alpha(theme.palette.error.main, 0.1),
                            backgroundImage: 'none',
                          }}
                        >
                          {r.success
                            ? <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                            : <DeleteIcon sx={{ fontSize: 14, color: 'error.main' }} />}
                          <Typography
                            variant="caption"
                            sx={{ color: r.success ? 'success.dark' : 'error.dark', fontSize: 12 }}
                          >
                            {r.email} - {r.success ? 'Sent' : (r.error || 'Failed')}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>

              </Box>
            )}

            {/* Preview panel */}
            {tab === 'preview' && (
              <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {previewLoading ? (
                  <Box display="flex" alignItems="center" justifyContent="center" flex={1} gap={1.5}>
                    <CircularProgress size={24} sx={{ color: 'primary.main' }} />
                    <Typography sx={{ color: 'text.disabled', fontSize: 13 }}>Rendering preview…</Typography>
                  </Box>
                ) : previewError ? (
                  <ErrorAlert error={previewError} onClose={() => setPreviewError(null)} />
                ) : previewHtml ? (
                  <>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1.5,
                        bgcolor: 'action.hover',
                        backgroundImage: 'none',
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <SubjectIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mr: 0.5 }}>
                        Subject:
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.primary', fontSize: 13 }}>
                        {previewSubject}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        bgcolor: 'action.hover',
                        backgroundImage: 'none',
                      }}
                    >
                      <iframe
                        srcDoc={previewHtml}
                        title="Email Preview"
                        sandbox=""
                        referrerPolicy="no-referrer"
                        style={{ width: '100%', height: '100%', minHeight: 360, border: 'none' }}
                      />
                    </Box>
                  </>
                ) : (
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1} gap={1}>
                    <PreviewIcon sx={{ fontSize: 40, color: 'action.disabled' }} />
                    <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                      Click Preview to render the email
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handlePreview}
                      sx={{
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        textTransform: 'none',
                        borderRadius: '8px',
                        mt: 0.5,
                        '&:hover': {
                          borderColor: 'primary.dark',
                          bgcolor: alpha(theme.palette.primary.main, 0.06),
                          backgroundImage: 'none',
                        },
                      }}
                    >
                      Load Preview
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          sx={{ borderRadius: 2, boxShadow: theme.shadows[8] }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default CompanyEmailTemplates
